import * as React from 'react';
import { useNavigate } from 'react-router';

import BhanzuLCLogo from '@/assets/logos/bhanzu-lc.svg';
import BhanzuLogo from '@/assets/logos/bhanzu-logo.svg';
import { Avatar } from '@/components/ui/avatar';
import { MenuTrigger } from '@/components/ui/menu';
import { env } from '@/config/env';
import { paths } from '@/config/paths';
import { useProfilesData } from '@/features/user';
import {
  AvatarLegacyKey,
  avatarMapLegacy,
} from '@/features/user/components/profile-avatar/avatar-map-legacy';
import { VariantDropdown } from '@/layouts/main-layout/components/variant-dropdown';
import { useTourStore } from '@/stores/tour';
import { cn } from '@/utils';

import { Navigation } from '../navigation';
import { NotificationUI } from '../navigation/notification-ui';

import UserMenu from './user-menu';
import { getCreditText } from './utils';

export interface MainHeaderProps extends React.HTMLAttributes<HTMLElement> {
  activeTab?: 'home' | 'modules' | 'brain-gym' | 'notification';
}

const MainHeader = React.forwardRef<HTMLElement, MainHeaderProps>(
  ({ className, ...props }, _ref) => {
    const navigate = useNavigate();
    const { enrolledCourses, selectedProfile, selectedCourse } =
      useProfilesData();
    const { run: isTourRunning, stepIndex, steps } = useTourStore();

    const currentTarget = steps[stepIndex]?.target;
    const isTourDropdownTarget =
      currentTarget === '#tour-my-profiles' ||
      currentTarget === '#tour-switch-profile';

    const handleLogoClick = async () => {
      await navigate(paths.app.root.getHref());
    };

    let avatarLeftText = '';

    if (env.IS_OFFLINE) {
      if (selectedProfile?.absolute_grade) {
        avatarLeftText = `Level ${selectedProfile?.absolute_grade}`;
      }
    } else {
      avatarLeftText = getCreditText(selectedCourse);
    }

    const avatar = selectedProfile?.avatar ?? 'bird0001';
    // Check if avatar exists in avatarMapLegacy, otherwise use default
    const avatarKey = (
      avatar in avatarMapLegacy ? avatar : 'bird0001'
    ) as AvatarLegacyKey;

    return (
      <div className="w-full flex justify-between gap-4">
        <header
          className={cn(
            'w-full h-20 bg-magical-moonlight-s px-6 flex items-center justify-between rounded-3xl',
            className
          )}
          {...props}
        >
          {/* Logo - progressively smaller, then hidden */}
          <button
            className="flex-shrink-0 hidden sm:block"
            onClick={() => void handleLogoClick()}
          >
            <img
              alt="Bhanzu logo"
              src={env.IS_OFFLINE ? BhanzuLCLogo : BhanzuLogo}
              className="xl:w-32 lg:w-28 md:w-24 sm:w-20 h-auto"
            />
          </button>

          <div className="h-full">
            <Navigation />
          </div>

          {/* Profile - responsive sizing then hiding */}
          <div className="flex items-center space-x-2 md:space-x-4">
            {/* Notification Icon */}
            <NotificationUI />

            <div
              className={`hidden md:flex md:items-center md:space-x-3 ${avatarLeftText && 'border border-black rounded-2xl'}`}
            >
              {/* Level indicator */}
              {avatarLeftText ? (
                <div className="px-4 py-1">
                  <span className="text-sm font-medium">{avatarLeftText}</span>
                </div>
              ) : null}
              {/* User Profile - smaller on smaller screens */}
              <MenuTrigger
                menu={<UserMenu />}
                align="end"
                isOpen={
                  isTourRunning && isTourDropdownTarget ? true : undefined
                }
              >
                <Avatar
                  icon={avatarMapLegacy[avatarKey]}
                  size="md"
                  className="scale-105 hover:scale-110  sm:w-12 sm:h-12 md:w-14 md:h-14 lg:w-16 lg:h-16 border border-magical-moonlight-d1"
                />
              </MenuTrigger>
            </div>
          </div>
        </header>

        {/* Course Selection Dropdown */}
        {enrolledCourses && enrolledCourses.length > 1 ? (
          <div className="hidden sm:block h-20" id="tour-course-selection">
            <VariantDropdown className="w-36 md:w-36 lg:w-40" size="full" />
          </div>
        ) : null}
      </div>
    );
  }
);

MainHeader.displayName = 'MainHeader';

export { MainHeader };
